/**
 * Owns the editor state for flags, follow-up, and tags — three independent
 * dimensions that share a hydration cadence and the same panel surface.
 *
 * Exposes:
 *  - state atoms + setters
 *  - hydration effects from `currentReport`
 *  - derived: `orderedSections`, `tagsSection`, `tagsCatalog`,
 *    `disabledFlags`, `disabledFlagHints`, `followUpGroups`,
 *    `buildFlagsPayload`
 *  - handlers: flag toggle, follow-up change, tags change (also mirrors
 *    tags into `currentReport` so the baseline fingerprint sees the edit)
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { commitHistoryStep } from '@/features/study-detail/history/historyStore';
import { createWorksheetStep } from '@/features/study-detail/history/worksheetSteps';
import { getWorksheetSnapshot } from '@/features/study-detail/history/worksheetApplier';
import { computeFlagRestrictions } from '@/features/report';
import { FollowUp, Report as MReport } from '@/models/report';
import { FollowUpGroup, TemplateSection, Worksheet } from '@/models/template';
import {
  serializeFollowUp,
  type ReportEditBaseline,
} from '../utils/reportEditBaseline';

interface UseReportFlagsArgs {
  currentReport: MReport | null;
  setCurrentReport: (
    next: MReport | ((prev: MReport | null) => MReport | null)
  ) => void;
  reportWorksheet: Worksheet | undefined;
  userRole: string;
  /** Dirty anchor: local flag/follow-up state diverging from this baseline
   *  is an unsaved edit and must survive a `currentReport` change. */
  serverBaseline: ReportEditBaseline | null;
}

export interface ReportFlagsState {
  selectedFlags: Set<string>;
  setSelectedFlags: React.Dispatch<React.SetStateAction<Set<string>>>;
  followUp: FollowUp | null;
  setFollowUp: React.Dispatch<React.SetStateAction<FollowUp | null>>;
  reportTags: string[];
  setReportTags: React.Dispatch<React.SetStateAction<string[]>>;
  orderedSections: TemplateSection[];
  tagsSection: TemplateSection | undefined;
  tagsCatalog: string[];
  disabledFlags: Set<string>;
  disabledFlagHints: Map<string, string>;
  followUpGroups: FollowUpGroup[];
  buildFlagsPayload: () => { flagClass: string }[];
  handleFlagToggle: (flagClass: string, checked: boolean) => void;
  handleFollowUpChange: (value: FollowUp | null) => void;
  handleTagsChange: (next: string[]) => void;
}

export function useReportFlags({
  currentReport,
  setCurrentReport,
  reportWorksheet,
  userRole,
  serverBaseline,
}: UseReportFlagsArgs): ReportFlagsState {
  const [selectedFlags, setSelectedFlags] = useState<Set<string>>(new Set());
  const [followUp, setFollowUp] = useState<FollowUp | null>(null);
  const [reportTags, setReportTags] = useState<string[]>([]);

  const orderedSections = useMemo<TemplateSection[]>(() => {
    const sections = reportWorksheet?.sections ?? [];
    return [...sections].sort((a, b) => a.index - b.index);
  }, [reportWorksheet?.sections]);

  const tagsSection = useMemo(
    () => orderedSections.find((s) => s.sectionType === 'TAGS'),
    [orderedSections]
  );

  const tagsCatalog = useMemo<string[]>(() => {
    if (!tagsSection) return [];
    const groups = (tagsSection.groups ?? []) as { value?: string }[];
    return groups.map((g) => g.value ?? '').filter((v) => v.length > 0);
  }, [tagsSection]);

  const buildFlagsPayload = useCallback(
    (): { flagClass: string }[] =>
      Array.from(selectedFlags)
        .filter((fc) => !fc.includes('FOLLOW_UP'))
        .map((flagClass) => ({ flagClass })),
    [selectedFlags]
  );

  // AP-773 (A8): each of these controls commits immediately — one step per
  // click / selection, no coalescing — recorded only when the state changes.
  const recordA8 = useCallback((label: 'flag' | 'follow-up' | 'tag') => {
    const before = getWorksheetSnapshot();
    if (before) commitHistoryStep(createWorksheetStep('A8', before, label));
  }, []);

  const handleFlagToggle = useCallback(
    (flagClass: string, checked: boolean) => {
      if (selectedFlags.has(flagClass) !== checked) recordA8('flag');
      setSelectedFlags((prev) => {
        const next = new Set(prev);
        if (checked) next.add(flagClass);
        else next.delete(flagClass);
        return next;
      });
    },
    [selectedFlags, recordA8]
  );

  const handleFollowUpChange = useCallback(
    (value: FollowUp | null) => {
      if (serializeFollowUp(value) !== serializeFollowUp(followUp))
        recordA8('follow-up');
      setFollowUp(value);
    },
    [followUp, recordA8]
  );

  const handleTagsChange = useCallback(
    (next: string[]) => {
      if (next.join('|') !== reportTags.join('|')) recordA8('tag');
      setReportTags(next);
      // Mirror into currentReport so the baseline fingerprint (which reads
      // from currentReport.tags) registers the edit and derived dirty flips.
      setCurrentReport((prev) => (prev ? { ...prev, tags: next } : prev));
    },
    [reportTags, setCurrentReport, recordA8]
  );

  // Ref mirror of the baseline, updated *after* the hydrate effect below so
  // the dirty check compares against the baseline the edits were made under
  // (the prop reseeds in the same commit as `currentReport` on refetch).
  const baselineRef = useRef(serverBaseline);

  // Hydrate selectedFlags + followUp from the report payload, keeping local
  // edits that diverge from the baseline (unsaved, must not be clobbered).
  //
  // Done during render rather than in an effect: `serverBaseline` is seeded in
  // the same commit that sets `currentReport`, so an effect adopts one commit
  // later and the report renders dirty in between — a flagged study flashed
  // "unsaved changes" on every load (AP-683). React re-runs this function
  // before committing, so the skewed pass is never shown.
  const [hydratedFrom, setHydratedFrom] = useState<MReport | null | undefined>(
    undefined
  );
  if (currentReport !== hydratedFrom) {
    setHydratedFrom(currentReport);
    // Still the baseline the local edits were made under — its mirror effect
    // below runs after this commit.
    const baseline = baselineRef.current;
    setSelectedFlags((prev) => {
      const prevSorted = Array.from(prev)
        .filter((fc) => !fc.includes('FOLLOW_UP'))
        .sort()
        .join('|');
      if (baseline && prevSorted !== baseline.flagsSorted) {
        return prev;
      }
      return new Set((currentReport?.flags ?? []).map((f) => f.flagClass));
    });
    setFollowUp((prev) => {
      if (baseline && serializeFollowUp(prev) !== baseline.followUpSerialized) {
        return prev;
      }
      return currentReport?.followUp ?? null;
    });
  }

  useEffect(() => {
    baselineRef.current = serverBaseline;
  }, [serverBaseline]);

  // Hydrate local tag state from the server response.
  useEffect(() => {
    setReportTags(currentReport?.tags ?? []);
  }, [currentReport?.tags]);

  const { disabledFlags, disabledFlagHints } = useMemo(
    () => computeFlagRestrictions(reportWorksheet?.flagGroups ?? [], userRole),
    [userRole, reportWorksheet?.flagGroups]
  );

  const followUpGroups = useMemo<FollowUpGroup[]>(() => {
    const section = reportWorksheet?.sections?.find(
      (s) => s.sectionType === 'FOLLOW_UP'
    );
    if (section?.followUpGroups?.length) return section.followUpGroups;
    return reportWorksheet?.followUpGroups ?? [];
  }, [reportWorksheet]);

  return {
    selectedFlags,
    setSelectedFlags,
    followUp,
    setFollowUp,
    reportTags,
    setReportTags,
    orderedSections,
    tagsSection,
    tagsCatalog,
    disabledFlags,
    disabledFlagHints,
    followUpGroups,
    buildFlagsPayload,
    handleFlagToggle,
    handleFollowUpChange,
    handleTagsChange,
  };
}
